import { useEffect, useState, type FormEvent } from "react"
import { useMutation, useQuery } from "@tanstack/react-query"
import { Link, useNavigate, useSearchParams } from "react-router-dom"
import { apiRequest } from "../lib/api"
import type { ApiError } from "../lib/api"
import type { User } from "../types"

type AuthResponse = {
  user: User
}

type SignUpProps = {
  onAuth: (user: User) => void
}

type InvitationInfo = {
  invitation: {
    email: string
    account_name: string
    expires_at: string
  }
}

const SignUp = ({ onAuth }: SignUpProps) => {
  const [searchParams] = useSearchParams()
  const invitationToken = searchParams.get("invitation")

  const [email, setEmail] = useState("")
  const [password, setPassword] = useState("")
  const [passwordConfirmation, setPasswordConfirmation] = useState("")
  const [error, setError] = useState<string | null>(null)
  const navigate = useNavigate()

  const invitationQuery = useQuery<InvitationInfo, ApiError>({
    queryKey: ["invitation-info", invitationToken],
    queryFn: () => apiRequest<InvitationInfo>(`/api/invitations/${invitationToken}/info`),
    enabled: Boolean(invitationToken),
    retry: false,
  })

  useEffect(() => {
    if (invitationQuery.data?.invitation?.email) {
      setEmail(invitationQuery.data.invitation.email)
    }
  }, [invitationQuery.data])

  const signUpMutation = useMutation<AuthResponse, ApiError>({
    mutationFn: () =>
      apiRequest<AuthResponse>("/api/sign_up", {
        method: "POST",
        body: JSON.stringify({
          user: {
            email,
            password,
            password_confirmation: passwordConfirmation,
            ...(invitationToken ? { invitation_token: invitationToken } : {}),
          },
        }),
      }),
    onSuccess: (data) => {
      onAuth(data.user)
      navigate("/", { replace: true })
    },
    onError: (err) => {
      setError(err.message ?? "Sign up failed")
    },
  })

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    setError(null)
    signUpMutation.mutate()
  }

  const isInvitation = Boolean(invitationToken)
  const invitationData = invitationQuery.data?.invitation

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4 py-10 text-foreground">
      <div className="w-full max-w-md rounded-lg border bg-card text-card-foreground shadow-xs">
        <div className="space-y-1 p-6">
          <h1 className="text-2xl font-semibold tracking-tight">
            {isInvitation ? "Aceitar Convite" : "Criar conta"}
          </h1>
          <p className="text-sm text-muted-foreground">
            {isInvitation && invitationData
              ? `Você foi convidado para a conta "${invitationData.account_name}".`
              : "Comece sua conta em poucos segundos."}
          </p>
        </div>
        <div className="space-y-4 p-6 pt-0">
          {invitationQuery.isError ? (
            <div className="rounded-md border border-destructive/50 bg-destructive/10 p-3 text-sm text-destructive">
              Convite inválido ou expirado.
            </div>
          ) : null}
          {error ? (
            <div className="rounded-md border border-destructive/50 bg-destructive/10 p-3 text-sm text-destructive">
              {error}
            </div>
          ) : null}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <label className="text-sm font-medium leading-none" htmlFor="email">
                Email
              </label>
              <input
                id="email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                disabled={isInvitation && Boolean(invitationData)}
                className="flex h-10 w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-xs placeholder:text-muted-foreground focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-60"
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium leading-none" htmlFor="password">
                Password
              </label>
              <input
                id="password"
                type="password"
                autoComplete="new-password"
                required
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                className="flex h-10 w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-xs placeholder:text-muted-foreground focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring"
              />
            </div>
            <div className="space-y-2">
              <label
                className="text-sm font-medium leading-none"
                htmlFor="password_confirmation"
              >
                Confirm password
              </label>
              <input
                id="password_confirmation"
                type="password"
                autoComplete="new-password"
                required
                value={passwordConfirmation}
                onChange={(event) => setPasswordConfirmation(event.target.value)}
                className="flex h-10 w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-xs placeholder:text-muted-foreground focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring"
              />
            </div>
            <button
              type="submit"
              disabled={signUpMutation.isPending || (isInvitation && invitationQuery.isError)}
              className="inline-flex h-10 w-full items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground shadow-sm hover:opacity-90 disabled:opacity-60"
            >
              {signUpMutation.isPending
                ? "Criando..."
                : isInvitation
                  ? "Aceitar Convite"
                  : "Criar conta"}
            </button>
          </form>
        </div>
        <div className="p-6 pt-0 text-sm text-muted-foreground">
          <span>Já tem uma conta?</span>{" "}
          <Link
            to="/sign-in"
            className="text-primary underline-offset-4 hover:underline"
          >
            Entrar
          </Link>
        </div>
      </div>
    </div>
  )
}

export default SignUp
